import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { DndContext, closestCorners, DragOverlay, useDroppable } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { Add, ArrowRight2 } from "iconsax-react";
import { Button } from "@/shared/ui";
import type { TaskListItem, TaskStatus } from "../../../types";
import type { ProjectBoardView } from "../../../hooks/useCrossProjectBoard";
import { useKanbanDnd } from "../../../hooks";
import { TASK_STATUS_MAP, COLUMN_HEADER_STYLES } from "../../../consts";
import KanbanTaskCard from "../../../components/KanbanTaskCard";

interface DroppableColumnProps {
  status: string;
  className?: string;
  style?: React.CSSProperties;
  children: React.ReactNode;
}

function DroppableColumn({ status, className, style, children }: DroppableColumnProps) {
  const { setNodeRef } = useDroppable({ id: status });
  return (
    <div ref={setNodeRef} className={className} style={style}>
      {children}
    </div>
  );
}

interface ProjectKanbanRowProps {
  board: ProjectBoardView;
  isEven: boolean;
  onTaskClick: (task: TaskListItem, projectId: number) => void;
  onCreateTask?: (status: TaskStatus, projectId: number) => void;
  onReorderError: () => void;
  canReorder: boolean;
  canDragAll: boolean;
  currentUserId: number | null;
  isBacklogActive: boolean;
  onNavigateToProject?: (projectId: number) => void;
  showHeading?: boolean;
}

export default function ProjectKanbanRow({
  board,
  isEven,
  onTaskClick,
  onCreateTask,
  onReorderError,
  canReorder,
  canDragAll,
  currentUserId,
  isBacklogActive,
  onNavigateToProject,
  showHeading = true,
}: ProjectKanbanRowProps) {
  const { t: tTasks } = useTranslation("TasksPage");
  const { t: tBoard } = useTranslation("BoardPage");

  const dndColumns = useMemo(() => {
    if (isBacklogActive) {
      return [board.backlogColumn, ...board.columns];
    }
    return board.columns;
  }, [isBacklogActive, board.backlogColumn, board.columns]);

  const {
    localColumns: allLocalColumns,
    activeTask,
    sensors,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
  } = useKanbanDnd({
    projectId: board.project.id,
    columns: dndColumns,
    onReorderError,
  });

  const localBacklogColumn = allLocalColumns.find((c) => c.status === "backlog") ?? null;
  const localColumns = allLocalColumns.filter((c) => c.status !== "backlog");

  function handleClick(task: TaskListItem) {
    onTaskClick(task, board.project.id);
  }

  const showBacklog = isBacklogActive && localBacklogColumn;
  const backlogStyles = COLUMN_HEADER_STYLES.backlog;

  const mainMaxCards = Math.max(1, ...localColumns.map((c) => c.tasks.length));
  const mainMaxCardsRef = useRef(mainMaxCards);
  if (!activeTask) {
    mainMaxCardsRef.current = mainMaxCards;
  } else {
    mainMaxCardsRef.current = Math.max(mainMaxCardsRef.current, mainMaxCards);
  }
  const stableMaxCards = mainMaxCardsRef.current;
  const colCount = localColumns.length + (showBacklog ? 1 : 0);

  const gridColumns = `repeat(${colCount}, minmax(200px, 1fr))`;

  const rowContent = (
    <div className="overflow-x-auto min-h-[120px]">
      <div className="grid pb-2 border-b surface-component-stroke gap-x-5" style={{ gridTemplateColumns: gridColumns }}>
        {showBacklog && (
          <div>
            <div className="flex justify-center items-center gap-1.5">
              <p className="text-body-bold-md content-base-primary">{tTasks(TASK_STATUS_MAP.backlog.label)}</p>
              <span
                className={cn(
                  "w-6.5 h-5 radius-2xs flex justify-center items-center text-label-xs",
                  backlogStyles.badge,
                )}>
                {localBacklogColumn!.tasks.length}
              </span>
            </div>
          </div>
        )}
        {localColumns.map((column) => {
          const styles = COLUMN_HEADER_STYLES[column.status];
          return (
            <div key={column.status}>
              <div className="flex justify-center items-center gap-1.5">
                <p className="text-body-bold-md content-base-primary">
                  {tTasks(TASK_STATUS_MAP[column.status].label)}
                </p>
                <span
                  className={cn("w-6.5 h-5 radius-2xs flex justify-center items-center text-label-xs", styles.badge)}>
                  {column.tasks.length}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      <div
        className="grid mt-3 gap-x-5"
        style={{
          gridTemplateColumns: gridColumns,
          gridTemplateRows: `repeat(${stableMaxCards}, auto)${onCreateTask ? " auto" : ""}`,
          rowGap: "8px",
        }}>
        {showBacklog && (
          <div
            className="surface-component-fill radius-md p-2 flex flex-col"
            style={{ gridColumn: 1, gridRow: "1 / -1" }}>
            <DroppableColumn status="backlog" className="flex flex-col gap-2 min-h-[60px] flex-1">
              <SortableContext
                items={localBacklogColumn!.tasks.map((t) => t.id)}
                strategy={verticalListSortingStrategy}>
                {localBacklogColumn!.tasks.map((task) => (
                  <KanbanTaskCard
                    key={task.id}
                    task={task}
                    projectName={board.project.name}
                    onTaskClick={handleClick}
                    canDragAll={canDragAll}
                    currentUserId={currentUserId}
                  />
                ))}
              </SortableContext>
              {onCreateTask && (
                <button
                  type="button"
                  onClick={() => onCreateTask("backlog" as TaskStatus, board.project.id)}
                  className="mt-auto w-full h-6 radius-2xs border border-dashed surface-component-stroke flex justify-center items-center gap-1 content-base-low cursor-pointer">
                  <Add size={12} color="currentColor" />
                  <p className="text-label-xs content-base-low">{tBoard("kanban.addTask")}</p>
                </button>
              )}
            </DroppableColumn>
          </div>
        )}
        {localColumns.map((column, idx) => {
          const colIdx = showBacklog ? idx + 2 : idx + 1;
          const taskCount = column.tasks.length;
          return (
            <DroppableColumn
              key={column.status}
              status={column.status}
              className={cn("grid grid-rows-subgrid", taskCount === 0 && "min-h-[60px]")}
              style={{
                gridColumn: colIdx,
                gridRow: `1 / span ${Math.max(taskCount, 1)}`,
              }}>
              <SortableContext items={column.tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
                {column.tasks.map((task) => (
                  <KanbanTaskCard
                    key={task.id}
                    task={task}
                    projectName={board.project.name}
                    onTaskClick={handleClick}
                    canDragAll={canDragAll}
                    currentUserId={currentUserId}
                  />
                ))}
              </SortableContext>
            </DroppableColumn>
          );
        })}
        {onCreateTask &&
          localColumns.map((column, idx) => {
            const colIdx = showBacklog ? idx + 2 : idx + 1;
            return (
              <button
                key={`add-${column.status}`}
                type="button"
                onClick={() => onCreateTask(column.status, board.project.id)}
                className="w-full h-6 radius-2xs border border-dashed surface-component-stroke flex justify-center items-center gap-1 content-base-low cursor-pointer mb-2"
                style={{ gridColumn: colIdx, gridRow: stableMaxCards + 1 }}>
                <Add size={12} color="currentColor" />
                <p className="text-label-xs content-base-low">{tBoard("kanban.addTask")}</p>
              </button>
            );
          })}
      </div>
    </div>
  );

  return (
    <div
      className={cn(
        "radius-xl not-last:mb-5",
        showHeading && "p-5",
        showHeading && (isEven ? "surface-component-fill" : "border surface-component-stroke"),
      )}>
      {showHeading && (
        <div className="flex items-center justify-between gap-2 mb-4">
          <h3 className="text-display-2xs content-base-primary">{board.project.name}</h3>
          {onNavigateToProject && (
            <Button
              variant="secondary"
              isIconButton
              className="w-5! h-5! p-0! radius-xs flex justify-center items-center content-base-secondary"
              onClick={() => onNavigateToProject(board.project.id)}>
              <ArrowRight2 size={12} color="currentColor" />
            </Button>
          )}
        </div>
      )}
      {canReorder ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}>
          {rowContent}
          <DragOverlay>
            {activeTask ? <KanbanTaskCard task={activeTask} projectName={board.project.name} isDragOverlay /> : null}
          </DragOverlay>
        </DndContext>
      ) : (
        rowContent
      )}
    </div>
  );
}
